import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import store from '../store/store';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import { uploadFileToSharePoint, fetchUploadedFiles, deleteFileFromSharePoint, type FileDisplayData } from '../Services/BakingAndCoolingFileUpload';
import { collectEstimationDataCycleSave, savesectionApicall, fetchCycleData, type CycleData } from '../Services/BakingAndCoolingRecord';
import { addOfflineData } from '../store/BakingAndCoolingSlice';
import { storeFileOffline, getFileDisplayData } from '../Services/BakingAndCoolingOfflineFileUpload';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';
import { SP_SITE_URL, SHAREPOINT_LIBRARIES } from '../constants/sharepoint';

const BAKING_COOLING_INITIAL_KEY = 'bakingCoolingInitialData';

interface BakingCoolingInitialData {
    productName: string;
    lineNumber: string;
    executiveName: string;
    bakingTime: string;
}

function getBakingCoolingInitial(): BakingCoolingInitialData | null {
    try {
        const s = localStorage.getItem(BAKING_COOLING_INITIAL_KEY);
        return s ? (JSON.parse(s) as BakingCoolingInitialData) : null;
    } catch {
        return null;
    }
}

function saveBakingCoolingInitial(data: BakingCoolingInitialData): void {
    try {
        localStorage.setItem(BAKING_COOLING_INITIAL_KEY, JSON.stringify(data));
    } catch (e) {
        console.warn('Could not save Baking & Cooling initial data', e);
    }
}

/** Maximum number of cycles supported (array size; logic supports infinite cycles via numVisibleCycles) */
const MAX_CYCLES = 100;

const BakingAndCooling: React.FC = () => {
    console.log('BakingAndCooling component is rendering...');

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { accounts, instance } = useMsal();
    const { selectedCycle, plantTourId } = useSelector((state: RootState) => state.planTour ?? {});
    const user = useSelector((state: RootState) => state.user?.user);
    const isOfflineStarted = useSelector((state: RootState) => state.appState?.isOfflineStarted ?? false);
    const reduxFetchedCycles = useSelector((state: RootState) => state.bakingAndCooling?.fetchedCycles ?? []);

    // Format date as DD/MM/YYYY
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    // Cycle index helper: 1-based cycle number -> 0-based array index
    const idx = (cycle: number) => Math.max(0, Math.min(cycle - 1, MAX_CYCLES - 1));

    interface CycleProductData {
        cycleNum: string;
        product: string;
        executiveName: string;
        bakingTime: string;
        lineNumber: string;
        bakingTempZone1: string;
        bakingTempZone2: string;
        bakingTempZone3: string;
        bakingTempZone4: string;
        productTempAfterBaking: string;
        ap: string;
        std: string;
        ad: string;
        ovenFireStartTime: string;
        ovenStopTime: string;
        productInTime: string;
        productOutTime: string;
        coolingTime: string;
        coolingTemp: string;
        coreTemp: string;
        productColor: string;
    }

    const emptyStartForm = { productName: '', lineNumber: '', executiveName: '', bakingTime: '' };
    const emptySaveForm = {
        bakingTempZone1: '', bakingTempZone2: '', bakingTempZone3: '', bakingTempZone4: '',
        productTempAfterBaking: '', ap: '', std: '', ad: '',
        ovenFireStartTime: '', ovenStopTime: '', productInTime: '', productOutTime: '',
        coolingTime: '', coolingTemp: '', coreTemp: '', productColor: ''
    };

    // Build initial state from Redux when offline so first paint shows completed cycles (no flash of empty)
    const offlineInitial = useMemo(() => {
        try {
            const s = store.getState();
            if (!s?.appState?.isOfflineStarted) return null;
            const fetched = s?.bakingAndCooling?.fetchedCycles ?? [];
            if (!Array.isArray(fetched) || fetched.length === 0) return null;
            const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
            const idxFn = (c: number) => Math.max(0, Math.min(c - 1, MAX_CYCLES - 1));
            fetched.forEach((cycleData: any) => {
                let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
                const cycleNum = parseInt(cycleNumStr, 10);
                if (isNaN(cycleNum) || cycleNum < 1) return;
                const i = idxFn(cycleNum);
                if (i >= 0 && i < MAX_CYCLES) {
                    byCycle[i].push({
                        cycleNum: String(cycleNum),
                        product: cycleData.product || '',
                        executiveName: cycleData.executiveName || '',
                        bakingTime: cycleData.bakingTime || '',
                        lineNumber: cycleData.lineNumber || '',
                        bakingTempZone1: cycleData.bakingTempZone1 || '',
                        bakingTempZone2: cycleData.bakingTempZone2 || '',
                        bakingTempZone3: cycleData.bakingTempZone3 || '',
                        bakingTempZone4: cycleData.bakingTempZone4 || '',
                        productTempAfterBaking: cycleData.productTempAfterBaking || '',
                        ap: cycleData.ap || '',
                        std: cycleData.std || '',
                        ad: cycleData.ad || '',
                        ovenFireStartTime: cycleData.ovenFireStartTime || '',
                        ovenStopTime: cycleData.ovenStopTime || '',
                        productInTime: cycleData.productInTime || '',
                        productOutTime: cycleData.productOutTime || '',
                        coolingTime: cycleData.coolingTime || '',
                        coolingTemp: cycleData.coolingTemp || '',
                        coreTemp: cycleData.coreTemp || '',
                        productColor: cycleData.productColor || ''
                    });
                }
            });
            const completedCycles = Array(MAX_CYCLES).fill(false);
            byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
            const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
            return { savedProducts: byCycle, cycleCompleted: completedCycles, currentCycle: maxCycleFromData + 1 };
        } catch (e) {
            return null;
        }
    }, []);

    // Array-based state for n cycles - init from Redux when offline so completed cycles show on first paint
    const [cycleCompleted, setCycleCompleted] = useState<boolean[]>(() => offlineInitial?.cycleCompleted ?? Array(MAX_CYCLES).fill(false));
    const [isSessionStarted, setIsSessionStarted] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
    const [cycleExpanded, setCycleExpanded] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
    const [savedProducts, setSavedProducts] = useState<CycleProductData[][]>(() => offlineInitial?.savedProducts ?? Array(MAX_CYCLES).fill(null).map(() => []));
    const [showStartFormForNewProduct, setShowStartFormForNewProduct] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
    const [formDataByCycle, setFormDataByCycle] = useState<typeof emptyStartForm[]>(() => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptyStartForm })));
    const [saveFormDataByCycle, setSaveFormDataByCycle] = useState<typeof emptySaveForm[]>(() => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptySaveForm })));
    const [selectedFile, setSelectedFile] = useState<(File | null)[]>(() => Array(MAX_CYCLES).fill(null));
    const [isUploading, setIsUploading] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
    const [uploadedFiles, setUploadedFiles] = useState<FileDisplayData[][]>(() => Array(MAX_CYCLES).fill(null).map(() => []));
    /** Current cycle (1-based). Same pattern as CodeVerification: one active cycle, complete to move to next (6, 7, 8, ... n). */
    const [currentCycle, setCurrentCycle] = useState(() => offlineInitial?.currentCycle ?? 1);
    const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<number>>(new Set());
    // Setters that update by cycle number (1-based)
    const setCycleCompletedAt = (cycle: number, value: boolean) => setCycleCompleted(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
    const setIsSessionStartedAt = (cycle: number, value: boolean) => setIsSessionStarted(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
    const setCycleExpandedAt = (cycle: number, value: boolean) => setCycleExpanded(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
    const setSavedProductsAt = (cycle: number, updater: (prev: CycleProductData[]) => CycleProductData[]) => setSavedProducts(prev => { const n = prev.map(a => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });
    const setShowStartFormForNewProductAt = (cycle: number, value: boolean) => setShowStartFormForNewProduct(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
    const setFormDataByCycleAt = (cycle: number, value: typeof emptyStartForm) => setFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[idx(cycle)] = value; return n; });
    const setSaveFormDataByCycleAt = (cycle: number, value: typeof emptySaveForm) => setSaveFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[idx(cycle)] = value; return n; });
    const setSelectedFileAt = (cycle: number, value: File | null) => setSelectedFile(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
    const setIsUploadingAt = (cycle: number, value: boolean) => setIsUploading(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
    const setUploadedFilesAt = (cycle: number, updater: (prev: FileDisplayData[]) => FileDisplayData[]) => setUploadedFiles(prev => { const n = prev.map(a => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });

    // Helper function to format time input as HH:MM:SS
    const formatTimeInput = (value: string): string => {
        // Remove all non-digit characters
        const digits = value.replace(/\D/g, '');
        
        // Limit to 6 digits (HHMMSS)
        const limitedDigits = digits.slice(0, 6);
        
        // Format as HH:MM:SS
        if (limitedDigits.length === 0) {
            return '';
        } else if (limitedDigits.length <= 2) {
            return limitedDigits;
        } else if (limitedDigits.length <= 4) {
            return `${limitedDigits.slice(0, 2)}:${limitedDigits.slice(2)}`;
        } else {
            return `${limitedDigits.slice(0, 2)}:${limitedDigits.slice(2, 4)}:${limitedDigits.slice(4)}`;
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, cycle: number = 1, isSaveForm: boolean = false) => {
        const { name, value } = e.target;
        
        // Check if this is a time field
        const timeFields = ['bakingTime', 'coolingTime', 'ovenFireStartTime', 'ovenStopTime', 'productInTime', 'productOutTime'];
        const isTimeField = timeFields.includes(name);
        
        // Format time input if it's a time field
        const processedValue = isTimeField ? formatTimeInput(value) : value;
        
        const i = idx(cycle);
        if (isSaveForm) {
            setSaveFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[i] = { ...n[i], [name]: processedValue }; return n; });
        } else {
            setFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[i] = { ...n[i], [name]: processedValue }; return n; });
        }
    };

    const handleStartSession = (cycle: number = 1) => {
        const data = formDataByCycle[idx(cycle)];
        console.log(`Starting Baking & Cooling session for Cycle ${cycle}:`, data);
        saveBakingCoolingInitial({
            productName: data.productName || '',
            lineNumber: data.lineNumber || '',
            executiveName: data.executiveName || '',
            bakingTime: data.bakingTime || ''
        });
        setShowStartFormForNewProductAt(cycle, false);
        setIsSessionStartedAt(cycle, true);
        // Reset uploaded files and selected file when starting a new session
        setUploadedFilesAt(cycle, () => [] as FileDisplayData[]);
        setSelectedFileAt(cycle, null);
    };

    const handleCancelSession = (cycle: number = 1) => setIsSessionStartedAt(cycle, false);

    const handleSaveSession = async (cycle: number = 1) => {
        if (!plantTourId) {
            alert('Plant Tour ID is missing. Please start a plant tour first.');
            return;
        }

        const i = idx(cycle);
        const saveData = saveFormDataByCycle[i];
        const startData = formDataByCycle[i];
        
        // Get user name from Redux or MSAL accounts
        const userName = user?.Name || accounts[0]?.name || '';
        
        if (!userName) {
            alert('User name is missing. Please ensure you are logged in.');
            return;
        }

        console.log(`Saving Baking & Cooling session for Cycle ${cycle}:`, { startData, saveData });
        console.log('Current offline status:', isOfflineStarted);

        try {
            // Check if we're offline
            if (isOfflineStarted) {
                console.log('Processing in offline mode...');

                // Collect data for offline storage
                const { savedData } = await collectEstimationDataCycleSave(
                    cycle,
                    startData,
                    saveData,
                    plantTourId,
                    userName
                );

                // Store data for offline sync in Redux
                const offlineData = {
                    cycleNo: cycle,
                    records: [{
                        cycleNum: cycle.toString(),
                        product: startData.productName,
                        executiveName: startData.executiveName,
                        bakingTime: startData.bakingTime,
                        lineNumber: startData.lineNumber,
                        bakingTempZone1: saveData.bakingTempZone1,
                        bakingTempZone2: saveData.bakingTempZone2,
                        bakingTempZone3: saveData.bakingTempZone3,
                        bakingTempZone4: saveData.bakingTempZone4,
                        productTempAfterBaking: saveData.productTempAfterBaking,
                        ap: saveData.ap,
                        std: saveData.std,
                        ad: saveData.ad,
                        ovenFireStartTime: saveData.ovenFireStartTime,
                        ovenStopTime: saveData.ovenStopTime,
                        productInTime: saveData.productInTime,
                        productOutTime: saveData.productOutTime,
                        coolingTime: saveData.coolingTime,
                        coolingTemp: saveData.coolingTemp,
                        coreTemp: saveData.coreTemp,
                        productColor: saveData.productColor
                    }],
                    timestamp: Date.now()
                };
                console.log('Storing offline data in Redux:', offlineData);
                dispatch(addOfflineData(offlineData));

                // Multiple products per cycle: add product to array and show inline Add New / Complete Cycle in cycle section
                const productData: CycleProductData = {
                    cycleNum: cycle.toString(),
                    product: startData.productName,
                    executiveName: startData.executiveName,
                    bakingTime: startData.bakingTime,
                    lineNumber: startData.lineNumber,
                    bakingTempZone1: saveData.bakingTempZone1,
                    bakingTempZone2: saveData.bakingTempZone2,
                    bakingTempZone3: saveData.bakingTempZone3,
                    bakingTempZone4: saveData.bakingTempZone4,
                    productTempAfterBaking: saveData.productTempAfterBaking,
                    ap: saveData.ap,
                    std: saveData.std,
                    ad: saveData.ad,
                    ovenFireStartTime: saveData.ovenFireStartTime,
                    ovenStopTime: saveData.ovenStopTime,
                    productInTime: saveData.productInTime,
                    productOutTime: saveData.productOutTime,
                    coolingTime: saveData.coolingTime,
                    coolingTemp: saveData.coolingTemp,
                    coreTemp: saveData.coreTemp,
                    productColor: saveData.productColor
                };

                setSavedProductsAt(cycle, prev => [...prev, productData]);
                // Hide save form and show inline "Add New" / "Complete Cycle" inside the cycle (no modal)
                setIsSessionStartedAt(cycle, false);
                setShowStartFormForNewProductAt(cycle, false);

                // Store savedData in localStorage for sync
                try {
                    localStorage.setItem(`baking-cooling-cycle-${cycle}-data`, JSON.stringify(savedData));
                    console.log('Saved Baking & Cooling data to localStorage for sync:', savedData);
                } catch (e) {
                    console.warn('Failed to persist Baking & Cooling data for offline sync', e);
                }

                showOfflineSaveAlertForCategory(cycle, 'Baking and Cooling');
            } else {
                // Collect data for API
                const { savedData } = await collectEstimationDataCycleSave(
                    cycle,
                    startData,
                    saveData,
                    plantTourId,
                    userName
                );

                // Save to API
                const result = await savesectionApicall(savedData);

                if (result.success) {
                    // Multiple products per cycle: add product to array and show inline Add New / Complete Cycle in cycle section
                    const productData: CycleProductData = {
                        cycleNum: cycle.toString(),
                        product: startData.productName,
                        executiveName: startData.executiveName,
                        bakingTime: startData.bakingTime,
                        lineNumber: startData.lineNumber,
                        bakingTempZone1: saveData.bakingTempZone1,
                        bakingTempZone2: saveData.bakingTempZone2,
                        bakingTempZone3: saveData.bakingTempZone3,
                        bakingTempZone4: saveData.bakingTempZone4,
                        productTempAfterBaking: saveData.productTempAfterBaking,
                        ap: saveData.ap,
                        std: saveData.std,
                        ad: saveData.ad,
                        ovenFireStartTime: saveData.ovenFireStartTime,
                        ovenStopTime: saveData.ovenStopTime,
                        productInTime: saveData.productInTime,
                        productOutTime: saveData.productOutTime,
                        coolingTime: saveData.coolingTime,
                        coolingTemp: saveData.coolingTemp,
                        coreTemp: saveData.coreTemp,
                        productColor: saveData.productColor
                    };

                    setSavedProductsAt(cycle, prev => [...prev, productData]);
                    // Hide save form and show inline "Add New" / "Complete Cycle" inside the cycle (no modal)
                    setIsSessionStartedAt(cycle, false);
                    setShowStartFormForNewProductAt(cycle, false);
                } else {
                    alert(`Failed to save Cycle ${cycle}: ${result.message}`);
                }
            }
        } catch (error) {
            console.error('Error saving session:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to save session. Please try again.';
            alert(`Error: ${errorMessage}`);
        }
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/home');
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, cycle: number) => {
        const file = e.target.files?.[0];
        if (file) setSelectedFileAt(cycle, file);
    };

    const handleFileUpload = async (cycle: number) => {
        if (!plantTourId) {
            alert('Plant Tour ID is required for file upload');
            return;
        }

        const file = selectedFile[idx(cycle)];
        if (!file) {
            alert('Please select a file before uploading');
            return;
        }
        setIsUploadingAt(cycle, true);

        try {
            // Get product name from form - ensure it's trimmed and non-empty
            const productName = (formDataByCycle[idx(cycle)].productName || '').trim();
            const fileInput = document.getElementById(`attachment-${cycle}`) as HTMLInputElement;
            const file = fileInput?.files?.[0];
            
            // Ensure file is selected
            if (!file) {
                alert('Please select a file before uploading');
                setIsUploadingAt(cycle, false);
                return;
            }
            
            // Ensure product name is set before uploading
            if (!productName) {
                alert('Please enter a product name before uploading attachments');
                setIsUploadingAt(cycle, false);
                return;
            }
            
            // Log cycle number and product name for debugging
            console.log('Uploading file - Parameters:', { 
                cycle, 
                cycleNum: cycle, 
                cycleType: typeof cycle,
                productName, 
                fileName: file.name,
                plantTourId: plantTourId ? 'present' : 'missing',
                isOfflineStarted
            });
            
            // Ensure cycle is a valid number
            const cycleNumber = Number(cycle);
            if (isNaN(cycleNumber) || cycleNumber < 1) {
                alert(`Invalid cycle number: ${cycle}. Please refresh and try again.`);
                setIsUploadingAt(cycle, false);
                return;
            }
            
            // Check if we're offline FIRST - don't try to get token if offline
            if (isOfflineStarted) {
                console.log('Processing file upload in offline mode...');
                
                // Store file offline in Redux
                await storeFileOffline(cycleNumber, file, plantTourId, productName);
                
                // Get offline files for display
                const offlineFiles = getFileDisplayData(cycleNumber, productName);
                setUploadedFilesAt(cycle, prev => {
                    const existingIds = new Set(prev.map(f => f.id));
                    const newFiles = offlineFiles.filter(f => !existingIds.has(f.id));
                    return [...prev, ...newFiles];
                });
                
                alert(`File "${file.name}" stored offline for Cycle ${cycleNumber}, Product ${productName}. Will upload when online.`);
                
                // Clear file input
                setSelectedFileAt(cycle, null);
                if (fileInput) {
                    fileInput.value = '';
                }
                setIsUploadingAt(cycle, false);
                return; // Exit early for offline mode
            }

            // Online mode - get SharePoint access token using MSAL
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            const accessToken = response.accessToken;

            if (!accessToken) {
                throw new Error('Failed to get SharePoint access token');
            }

            const result = await uploadFileToSharePoint(cycleNumber, plantTourId, accessToken, productName);
            if (result.success) {
                // Show uploaded file immediately (optimistic update) so it appears in "Uploaded Files" right away
                const itemId = result.data?.itemId?.toString?.() ?? String(Date.now());
                const savedFileName = result.data?.fileName || file.name; // Use the filename with timestamp from server
                const newEntry: FileDisplayData = {
                    id: itemId,
                    cycleNum: cycle, // Ensure cycle number is set
                    productName: productName, // Always set product name (already validated above)
                    fileUrl: '',
                    fileName: savedFileName, // Use the filename with date/time that was saved
                    fileSize: file.size,
                    uploadTime: Date.now(),
                    isOffline: false
                };
                setUploadedFilesAt(cycle, prev => [...prev, newEntry]);
                console.log('File uploaded successfully:', { cycle, productName, fileName: file.name });
                alert(result.message);
                
                // Clear file input AFTER successful upload
                setSelectedFileAt(cycle, null);
                if (fileInput) {
                    fileInput.value = '';
                }
                
                // Refresh from server to get real fileUrl and ensure list is in sync
                await loadUploadedFiles();
            } else {
                console.error('File upload failed:', result.message);
                alert(result.message);
            }
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to upload file. Please try again.';
            
            // If error occurs and we're offline, try to store offline as fallback
            if (isOfflineStarted || errorMessage.includes('token') || errorMessage.includes('network')) {
                try {
                    const fileInput = document.getElementById(`attachment-${cycle}`) as HTMLInputElement;
                    const file = fileInput?.files?.[0];
                    const productName = (formDataByCycle[idx(cycle)].productName || '').trim();
                    const cycleNumber = Number(cycle);
                    
                    if (file && productName && !isNaN(cycleNumber) && cycleNumber >= 1) {
                        await storeFileOffline(cycleNumber, file, plantTourId, productName);
                        const offlineFiles = getFileDisplayData(cycleNumber, productName);
                        setUploadedFilesAt(cycle, prev => {
                            const existingIds = new Set(prev.map(f => f.id));
                            const newFiles = offlineFiles.filter(f => !existingIds.has(f.id));
                            return [...prev, ...newFiles];
                        });
                        alert(`File "${file.name}" stored offline for Cycle ${cycleNumber}, Product ${productName}. Will upload when online.`);
                        setSelectedFileAt(cycle, null);
                        if (fileInput) {
                            fileInput.value = '';
                        }
                        setIsUploadingAt(cycle, false);
                        return;
                    }
                } catch (offlineError) {
                    console.error('Failed to store file offline as fallback:', offlineError);
                }
            }
            
            alert(errorMessage);
        } finally {
            setIsUploadingAt(cycle, false);
        }
    };

    const handleCompleteCycle = async (cycle: number) => {
        const products = savedProducts[idx(cycle)];
        if (!products.length) { alert('Please save at least one product before completing the cycle.'); return; }
        setCycleCompletedAt(cycle, true);
        setIsSessionStartedAt(cycle, false);
        // Advance to next cycle (CodeVerification pattern: 6, 7, 8, ... n)
        if (cycle === currentCycle) setCurrentCycle(prev => prev + 1);
        // Refresh files from server when cycle is completed to ensure attachments are loaded (only if not offline)
        if (plantTourId && accounts.length > 0 && !isOfflineStarted) {
            await loadUploadedFiles();
        }
    };

    const toggleCompletedCycleExpansion = (cycleNum: number) => {
        setExpandedCompletedCycles(prev => {
            const next = new Set(prev);
            if (next.has(cycleNum)) next.delete(cycleNum);
            else next.add(cycleNum);
            return next;
        });
    };

    // Function to load uploaded files from SharePoint
    const loadUploadedFiles = async () => {
        if (!plantTourId) {
            console.log('loadUploadedFiles: No plantTourId, skipping');
            return;
        }

        console.log('loadUploadedFiles: Starting...', { plantTourId, isOfflineStarted, accountsLength: accounts.length });

        try {
            // Always load offline files first
            const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
            for (let cycle = 1; cycle <= MAX_CYCLES; cycle++) {
                const offlineFiles = getFileDisplayData(cycle);
                if (offlineFiles.length > 0) {
                    offlineFilesByCycle[cycle] = offlineFiles;
                    console.log(`Found ${offlineFiles.length} offline files for cycle ${cycle}`);
                }
            }

            // If offline, only use offline files
            if (isOfflineStarted) {
                console.log('Loading offline files only...');
                setUploadedFiles(prev => prev.map((p, i) => {
                    const cycleNum = i + 1;
                    const offlineFiles = offlineFilesByCycle[cycleNum] || [];
                    // Merge with existing optimistic updates
                    const existingIds = new Set(offlineFiles.map(f => f.id));
                    const optimisticOnly = p.filter(f => !existingIds.has(f.id));
                    return [...offlineFiles, ...optimisticOnly];
                }));
                return;
            }

            // If online, fetch from SharePoint and merge with offline files
            if (!accounts.length) {
                console.warn('loadUploadedFiles: No accounts available');
                return;
            }

            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            const accessToken = response.accessToken;

            if (accessToken) {
                console.log('Fetching files from SharePoint...');
                const files = await fetchUploadedFiles(plantTourId, accessToken);
                console.log(`Fetched ${files.length} files from SharePoint`);
                
                const merge = (prev: FileDisplayData[], fromServer: FileDisplayData[], offline: FileDisplayData[]) => {
                    const serverIds = new Set(fromServer.map((f) => f.id));
                    const offlineIds = new Set(offline.map((f) => f.id));
                    const optimisticOnly = prev.filter((f) => !serverIds.has(f.id) && !offlineIds.has(f.id));
                    return [...fromServer, ...offline, ...optimisticOnly];
                };
                setUploadedFiles(prev => prev.map((p, i) => {
                    const cycleNum = i + 1;
                    const fromServer = files.filter((f) => f.cycleNum === cycleNum);
                    const offline = offlineFilesByCycle[cycleNum] || [];
                    const merged = merge(p, fromServer, offline);
                    // Log for debugging
                    if (fromServer.length > 0 || offline.length > 0) {
                        console.log(`Cycle ${cycleNum}: Loaded ${fromServer.length} server files and ${offline.length} offline files. Total: ${merged.length}`);
                    }
                    return merged;
                }));
            } else {
                console.warn('loadUploadedFiles: No access token available');
            }
        } catch (error) {
            console.error('Error loading uploaded files:', error);
            // On error, still try to load offline files
            if (isOfflineStarted) {
                const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
                for (let cycle = 1; cycle <= MAX_CYCLES; cycle++) {
                    const offlineFiles = getFileDisplayData(cycle);
                    if (offlineFiles.length > 0) {
                        offlineFilesByCycle[cycle] = offlineFiles;
                    }
                }
                setUploadedFiles(prev => prev.map((p, i) => {
                    const cycleNum = i + 1;
                    const offlineFiles = offlineFilesByCycle[cycleNum] || [];
                    const existingIds = new Set(offlineFiles.map(f => f.id));
                    const optimisticOnly = p.filter(f => !existingIds.has(f.id));
                    return [...offlineFiles, ...optimisticOnly];
                }));
            }
        }
    };

    // Load uploaded files when component mounts or plantTourId changes
    useEffect(() => {
        if (!plantTourId) return;
        
        // CRITICAL: Don't call API if offline mode is active
        if (isOfflineStarted) {
            console.log('=== BAKING AND COOLING: OFFLINE MODE - LOADING FILES FROM REDUX ===');
            console.log('Checking Redux for offline files...');
            
            // Get all offline files from Redux
            const state = store.getState();
            const allOfflineFiles = state.bakingAndCooling?.offlineFiles || [];
            console.log(`Total offline files in Redux: ${allOfflineFiles.length}`);
            console.log('Offline files by cycle:', allOfflineFiles.reduce((acc: any, file: any) => {
                const cycle = file.cycleNum || 0;
                if (!acc[cycle]) acc[cycle] = [];
                acc[cycle].push({ id: file.id, fileName: file.fileName, productName: file.productName, previewUrl: file.previewUrl ? 'has URL' : 'no URL' });
                return acc;
            }, {}));
            
            // Load offline files from Redux only
            const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
            for (let cycle = 1; cycle <= MAX_CYCLES; cycle++) {
                const offlineFiles = getFileDisplayData(cycle);
                if (offlineFiles.length > 0) {
                    offlineFilesByCycle[cycle] = offlineFiles;
                    console.log(`Cycle ${cycle}: Found ${offlineFiles.length} files in Redux`, offlineFiles.map(f => ({ id: f.id, fileName: f.fileName, fileUrl: f.fileUrl ? 'has URL' : 'no URL' })));
                }
            }
            
            // Update uploadedFiles state with Redux data
            setUploadedFiles(prev => prev.map((p, i) => {
                const cycleNum = i + 1;
                const offlineFiles = offlineFilesByCycle[cycleNum] || [];
                const existingIds = new Set(offlineFiles.map(f => f.id));
                const optimisticOnly = p.filter(f => !existingIds.has(f.id));
                const merged = [...offlineFiles, ...optimisticOnly];
                
                if (offlineFiles.length > 0) {
                    console.log(`Cycle ${cycleNum}: Loaded ${offlineFiles.length} files from Redux, total: ${merged.length}`);
                }
                
                return merged;
            }));
            return;
        }
        
        // Online mode: need accounts to fetch from SharePoint
        if (!accounts.length) {
            console.warn('BakingAndCooling: No accounts available, skipping file load');
            return;
        }
        
        console.log('Loading uploaded files on mount/plantTourId change...');
        loadUploadedFiles();
    }, [plantTourId, accounts.length, isOfflineStarted]);

    // Reload files when coming back online (after sync)
    const prevIsOfflineStartedRef = useRef(isOfflineStarted);
    useEffect(() => {
        // If we just came back online (was offline, now online), reload files
        if (prevIsOfflineStartedRef.current && !isOfflineStarted && plantTourId && accounts.length > 0) {
            console.log('Back online - reloading files from SharePoint after sync...');
            // Small delay to ensure sync has completed
            const timer = setTimeout(() => {
                if (!isOfflineStarted) { // Double check offline status before calling API
                    loadUploadedFiles();
                }
            }, 2000);
            return () => clearTimeout(timer);
        }
        prevIsOfflineStartedRef.current = isOfflineStarted;
    }, [isOfflineStarted, plantTourId, accounts.length]);

    // File viewing state
    const [viewingFileLoading, setViewingFileLoading] = useState<string | null>(null);
    const [viewingFile, setViewingFile] = useState<{ file: FileDisplayData; blobUrl: string } | null>(null);

    // Check if file is an image
    const isImageFile = (fileName: string): boolean => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    const handleViewFile = async (file: FileDisplayData) => {
        if (!accounts.length) {
            console.error('Cannot view file: no accounts');
            return;
        }
        
        // If file is offline, use preview URL directly
        if (file.isOffline && file.fileUrl) {
            window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        
        // CRITICAL: Don't call API if offline mode is active (unless file is offline)
        if (isOfflineStarted && !file.isOffline) {
            alert('Cannot view online files in offline mode. Please cancel offline mode first.');
            return;
        }
        
        setViewingFileLoading(file.id);
        const spSiteUrl = SP_SITE_URL;
        const libraryTitle = SHAREPOINT_LIBRARIES.BAKING_AND_COOLING;
        try {
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            const accessToken = response.accessToken;
            if (!accessToken) {
                if (file.fileUrl) window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
                return;
            }
            let blob: Blob;
            if (file.fileUrl) {
                const baseUrl = file.fileUrl.replace(/\?.*$/, '');
                const urlObj = new URL(baseUrl);
                const serverRelativeUrl = urlObj.pathname;
                const fetchUrl = `${spSiteUrl}/_api/web/GetFileByServerRelativeUrl('${encodeURIComponent(serverRelativeUrl)}')/$value`;
                const res = await fetch(fetchUrl, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${accessToken}`, 'Accept': '*/*' },
                });
                if (!res.ok) {
                    const directRes = await fetch(baseUrl, {
                        method: 'GET',
                        headers: { 'Authorization': `Bearer ${accessToken}`, 'Accept': '*/*' },
                    });
                    if (!directRes.ok) {
                        window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
                        return;
                    }
                    blob = await directRes.blob();
                } else {
                    blob = await res.blob();
                }
            } else if (file.id) {
                // Completed screen / no fileUrl: fetch by list item id
                const byIdUrl = `${spSiteUrl}/_api/web/lists/getbytitle('${libraryTitle}')/items(${file.id})/File/$value`;
                const res = await fetch(byIdUrl, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${accessToken}`, 'Accept': '*/*' },
                });
                if (!res.ok) {
                    console.error('Fetch by item id failed:', res.status);
                    return;
                }
                blob = await res.blob();
            } else {
                return;
            }
            if (!blob) return;
            const blobUrl = URL.createObjectURL(blob);
            setViewingFile({ file, blobUrl });
        } catch (error) {
            console.error('Error viewing file:', error);
            if (file.fileUrl) window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
        } finally {
            setViewingFileLoading(null);
        }
    };

    // Handle file delete
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
    const handleDeleteFile = async (file: FileDisplayData) => {
        if (!window.confirm(`Are you sure you want to delete "${file.fileName}"?`)) {
            return;
        }

        // CRITICAL: Don't call API if offline mode is active
        if (isOfflineStarted) {
            alert('Cannot delete files in offline mode. Please cancel offline mode first.');
            return;
        }

        setDeletingFileId(file.id);
        try {
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            const accessToken = response.accessToken;

            if (!accessToken) {
                alert('Failed to get access token. Please try again.');
                return;
            }

            const result = await deleteFileFromSharePoint(file.id, accessToken);
            
            if (result.success) {
                alert('File deleted successfully');
                // Refresh uploaded files list
                await loadUploadedFiles();
            } else {
                alert(`Failed to delete file: ${result.message}`);
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete file. Please try again.';
            alert(errorMessage);
        } finally {
            setDeletingFileId(null);
        }
    };

    // Function to load saved cycle data from API
    const loadCycleData = async () => {
        if (!plantTourId) return;
        
        // CRITICAL: Don't call API if offline mode is active
        if (isOfflineStarted) {
            console.log('BakingAndCooling: Skipping API call - offline mode is active');
            return;
        }

        try {
            const cycles = await fetchCycleData(plantTourId);
            
            const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);

            cycles.forEach((cycleData: CycleData) => {
                const cycleNum = parseInt(cycleData.cycleNum);
                const i = idx(cycleNum);
                if (i < 0 || i >= MAX_CYCLES) return;
                const productData: CycleProductData = {
                    cycleNum: cycleData.cycleNum,
                    product: cycleData.product !== "N/A" ? cycleData.product : '',
                    executiveName: cycleData.executiveName !== "N/A" ? cycleData.executiveName : '',
                    bakingTime: cycleData.bakingTime !== "N/A" ? cycleData.bakingTime : '',
                    lineNumber: cycleData.lineNumber || '',
                    bakingTempZone1: cycleData.bakingtempzone1 || '',
                    bakingTempZone2: cycleData.bakingtempzone2 || '',
                    bakingTempZone3: cycleData.bakingtempzone3 || '',
                    bakingTempZone4: cycleData.bakingtempzone4 || '',
                    productTempAfterBaking: cycleData.producttempafterbaking || '',
                    ap: cycleData.pccf_ap || '',
                    std: cycleData.pccf_std || '',
                    ad: cycleData.pccf_ad || '',
                    ovenFireStartTime: cycleData.ovenfirestarttime || '',
                    ovenStopTime: cycleData.ovenstoptime || '',
                    productInTime: cycleData.productintime || '',
                    productOutTime: cycleData.productouttime || '',
                    coolingTime: cycleData.coolingtime || '',
                    coolingTemp: cycleData.coolingtemp || '',
                    coreTemp: cycleData.coretemp || '',
                    productColor: cycleData.productcolor || ''
                };
                byCycle[i].push(productData);
            });

            setSavedProducts(byCycle);
            setCycleCompleted(prev => prev.map((v, i) => byCycle[i].length > 0 ? true : v));
            // Set current cycle to next after highest loaded (same as CodeVerification)
            const maxCycleFromData = cycles.length
                ? Math.max(...cycles.map((cd: CycleData) => parseInt(String(cd.cycleNum), 10) || 0), 1)
                : 0;
            setCurrentCycle(maxCycleFromData + 1);
        } catch (error) {
            console.error('Error loading cycle data:', error);
        }
    };

    useEffect(() => {
        const anyStarted = isSessionStarted.some(Boolean);
        const anyCompleted = cycleCompleted.some(Boolean);
        // Only load uploaded files if not in offline mode (offline files are loaded separately)
        if ((anyStarted || anyCompleted) && plantTourId && accounts.length > 0 && !isOfflineStarted) {
            loadUploadedFiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSessionStarted, cycleCompleted, plantTourId, isOfflineStarted]);

    // CRITICAL: Sync from Redux in layout effect when offline so UI is correct before paint
    useLayoutEffect(() => {
        if (!plantTourId || !isOfflineStarted) return;
        const fetched = store.getState()?.bakingAndCooling?.fetchedCycles ?? [];
        if (!Array.isArray(fetched) || fetched.length === 0) return;
        const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
        fetched.forEach((cycleData: any) => {
            let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
            const cycleNum = parseInt(cycleNumStr, 10);
            if (isNaN(cycleNum) || cycleNum < 1) return;
            const i = idx(cycleNum);
            if (i >= 0 && i < MAX_CYCLES) {
                byCycle[i].push({
                    cycleNum: String(cycleNum),
                    product: cycleData.product || '',
                    executiveName: cycleData.executiveName || '',
                    bakingTime: cycleData.bakingTime || '',
                    lineNumber: cycleData.lineNumber || '',
                    bakingTempZone1: cycleData.bakingTempZone1 || '',
                    bakingTempZone2: cycleData.bakingTempZone2 || '',
                    bakingTempZone3: cycleData.bakingTempZone3 || '',
                    bakingTempZone4: cycleData.bakingTempZone4 || '',
                    productTempAfterBaking: cycleData.productTempAfterBaking || '',
                    ap: cycleData.ap || '',
                    std: cycleData.std || '',
                    ad: cycleData.ad || '',
                    ovenFireStartTime: cycleData.ovenFireStartTime || '',
                    ovenStopTime: cycleData.ovenStopTime || '',
                    productInTime: cycleData.productInTime || '',
                    productOutTime: cycleData.productOutTime || '',
                    coolingTime: cycleData.coolingTime || '',
                    coolingTemp: cycleData.coolingTemp || '',
                    coreTemp: cycleData.coreTemp || '',
                    productColor: cycleData.productColor || ''
                });
            }
        });
        const completedCycles = Array(MAX_CYCLES).fill(false);
        byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
        const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
        setSavedProducts(byCycle);
        setCycleCompleted(completedCycles);
        setCurrentCycle(maxCycleFromData + 1);
        const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
        for (let c = 1; c <= MAX_CYCLES; c++) {
            const files = getFileDisplayData(c);
            if (files.length > 0) offlineFilesByCycle[c] = files;
        }
        setUploadedFiles(prev => prev.map((p, i) => {
            const cycleNum = i + 1;
            const offlineFiles = offlineFilesByCycle[cycleNum] || [];
            const existingIds = new Set(offlineFiles.map(f => f.id));
            const optimisticOnly = p.filter(f => !existingIds.has(f.id));
            return [...offlineFiles, ...optimisticOnly];
        }));
    }, [plantTourId, isOfflineStarted]);

    // Load cycle data when component mounts or plantTourId changes (same pattern as Code Verification / ALC)
    useEffect(() => {
        if (!plantTourId) return;

        // CRITICAL: Offline = use Redux only, no API (same as Code Verification)
        if (isOfflineStarted) {
            // Read directly from store so we always get latest (handles rehydration / same-session updates)
            const fetchedFromStore = store.getState()?.bakingAndCooling?.fetchedCycles ?? [];
            const cyclesToUse = Array.isArray(fetchedFromStore) ? fetchedFromStore : [];
            console.log('=== BAKING AND COOLING: OFFLINE MODE - USING REDUX ONLY ===');
            console.log('Redux fetched cycles count:', cyclesToUse.length);

            if (cyclesToUse.length > 0) {
                // Convert Redux cycles to component format
                const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
                
                cyclesToUse.forEach((cycleData: any) => {
                    // Handle cycle number - remove 'Cycle-' prefix if present, convert to number
                    let cycleNumStr = String(cycleData.cycleNum || '1');
                    cycleNumStr = cycleNumStr.replace('Cycle-', '').trim();
                    const cycleNum = parseInt(cycleNumStr, 10);
                    
                    if (isNaN(cycleNum) || cycleNum < 1) {
                        console.warn('Invalid cycle number:', cycleData.cycleNum, 'skipping');
                        return;
                    }
                    
                    const i = idx(cycleNum);
                    
                    if (i >= 0 && i < MAX_CYCLES) {
                        const productData: CycleProductData = {
                            cycleNum: String(cycleNum),
                            product: cycleData.product || '',
                            executiveName: cycleData.executiveName || '',
                            bakingTime: cycleData.bakingTime || '',
                            lineNumber: cycleData.lineNumber || '',
                            bakingTempZone1: cycleData.bakingTempZone1 || '',
                            bakingTempZone2: cycleData.bakingTempZone2 || '',
                            bakingTempZone3: cycleData.bakingTempZone3 || '',
                            bakingTempZone4: cycleData.bakingTempZone4 || '',
                            productTempAfterBaking: cycleData.productTempAfterBaking || '',
                            ap: cycleData.ap || '',
                            std: cycleData.std || '',
                            ad: cycleData.ad || '',
                            ovenFireStartTime: cycleData.ovenFireStartTime || '',
                            ovenStopTime: cycleData.ovenStopTime || '',
                            productInTime: cycleData.productInTime || '',
                            productOutTime: cycleData.productOutTime || '',
                            coolingTime: cycleData.coolingTime || '',
                            coolingTemp: cycleData.coolingTemp || '',
                            coreTemp: cycleData.coreTemp || '',
                            productColor: cycleData.productColor || ''
                        };
                        byCycle[i].push(productData);
                        console.log(`✓ Loaded cycle ${cycleNum}, product: ${productData.product || 'N/A'}, index: ${i}`);
                    } else {
                        console.warn(`Cycle number ${cycleNum} out of range (index ${i}), skipping`);
                    }
                });
                
                // Update state with loaded data
                setSavedProducts(byCycle);
                
                // Mark cycles as completed if they have products - ensure array is correct length
                const completedCycles = Array(MAX_CYCLES).fill(false);
                byCycle.forEach((products, idx) => {
                    if (products.length > 0) {
                        completedCycles[idx] = true;
                    }
                });
                setCycleCompleted(completedCycles);
                
                // Calculate max cycle and set current cycle
                const cyclesWithData = byCycle.map((products, idx) => ({ cycleNum: idx + 1, count: products.length })).filter(c => c.count > 0);
                const maxCycleFromData = cyclesWithData.length > 0
                    ? Math.max(...cyclesWithData.map(c => c.cycleNum), 0)
                    : 0;
                setCurrentCycle(maxCycleFromData + 1);
                
                console.log('=== SUCCESSFULLY LOADED FROM REDUX ===');
                console.log('Total cycles loaded:', cyclesToUse.length);
                console.log('Cycles with data:', cyclesWithData);
                console.log('Max cycle number:', maxCycleFromData);
                console.log('Next cycle:', maxCycleFromData + 1);
                console.log('Completed cycles array:', completedCycles);
                console.log('Completed cycles (true values):', completedCycles.map((completed, idx) => ({ cycle: idx + 1, completed })).filter(c => c.completed));
                console.log('Saved products by cycle:', byCycle.map((products, idx) => ({ cycle: idx + 1, products: products.length })).filter(c => c.products > 0));
                
                // IMPORTANT: Load attachments from Redux after cycles are loaded
                // This ensures attachments are displayed for completed cycles in offline mode
                console.log('=== LOADING ATTACHMENTS FROM REDUX AFTER CYCLES LOADED ===');
                const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
                for (let cycle = 1; cycle <= MAX_CYCLES; cycle++) {
                    const offlineFiles = getFileDisplayData(cycle);
                    if (offlineFiles.length > 0) {
                        offlineFilesByCycle[cycle] = offlineFiles;
                        console.log(`Cycle ${cycle}: Found ${offlineFiles.length} files in Redux`);
                    }
                }
                
                // Update uploadedFiles state with Redux data
                setUploadedFiles(prev => prev.map((p, i) => {
                    const cycleNum = i + 1;
                    const offlineFiles = offlineFilesByCycle[cycleNum] || [];
                    const existingIds = new Set(offlineFiles.map(f => f.id));
                    const optimisticOnly = p.filter(f => !existingIds.has(f.id));
                    const merged = [...offlineFiles, ...optimisticOnly];
                    
                    if (offlineFiles.length > 0) {
                        console.log(`Cycle ${cycleNum}: Loaded ${offlineFiles.length} files from Redux, total: ${merged.length}`);
                    }
                    
                    return merged;
                }));
                
                console.log('State updated - completed cycles and attachments from Redux');
            } else {
                // Offline but no data in Redux: reset to clean state (same as other sections)
                console.log('No Baking and Cooling cycles in Redux for offline mode - starting from cycle 1');
                setSavedProducts(Array(MAX_CYCLES).fill(null).map(() => []));
                setCycleCompleted(Array(MAX_CYCLES).fill(false));
                setCurrentCycle(1);
                setUploadedFiles(Array.from({ length: MAX_CYCLES }, () => []));
            }
            return;
        }

        // Online: fetch from API (same as Code Verification fetchCycleDataFromAPI)
        loadCycleData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plantTourId, isOfflineStarted, reduxFetchedCycles?.length]);

    // When offline: re-sync from Redux after a short delay (handles persist rehydration or late store update)
    useEffect(() => {
        if (!plantTourId || !isOfflineStarted) return;
        const timer = setTimeout(() => {
            const fetched = store.getState()?.bakingAndCooling?.fetchedCycles ?? [];
            if (!Array.isArray(fetched) || fetched.length === 0) return;
            const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
            fetched.forEach((cycleData: any) => {
                let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
                const cycleNum = parseInt(cycleNumStr, 10);
                if (isNaN(cycleNum) || cycleNum < 1) return;
                const i = idx(cycleNum);
                if (i < 0 || i >= MAX_CYCLES) return;
                byCycle[i].push({
                    cycleNum: String(cycleNum),
                    product: cycleData.product || '',
                    executiveName: cycleData.executiveName || '',
                    bakingTime: cycleData.bakingTime || '',
                    lineNumber: cycleData.lineNumber || '',
                    bakingTempZone1: cycleData.bakingTempZone1 || '',
                    bakingTempZone2: cycleData.bakingTempZone2 || '',
                    bakingTempZone3: cycleData.bakingTempZone3 || '',
                    bakingTempZone4: cycleData.bakingTempZone4 || '',
                    productTempAfterBaking: cycleData.productTempAfterBaking || '',
                    ap: cycleData.ap || '',
                    std: cycleData.std || '',
                    ad: cycleData.ad || '',
                    ovenFireStartTime: cycleData.ovenFireStartTime || '',
                    ovenStopTime: cycleData.ovenStopTime || '',
                    productInTime: cycleData.productInTime || '',
                    productOutTime: cycleData.productOutTime || '',
                    coolingTime: cycleData.coolingTime || '',
                    coolingTemp: cycleData.coolingTemp || '',
                    coreTemp: cycleData.coreTemp || '',
                    productColor: cycleData.productColor || ''
                });
            });
            const completedCycles = Array(MAX_CYCLES).fill(false);
            byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
            const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
            setSavedProducts(byCycle);
            setCycleCompleted(completedCycles);
            setCurrentCycle(maxCycleFromData + 1);
            const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
            for (let c = 1; c <= MAX_CYCLES; c++) {
                const files = getFileDisplayData(c);
                if (files.length > 0) offlineFilesByCycle[c] = files;
            }
            setUploadedFiles(prev => prev.map((p, i) => {
                const cycleNum = i + 1;
                const offlineFiles = offlineFilesByCycle[cycleNum] || [];
                const existingIds = new Set(offlineFiles.map(f => f.id));
                const optimisticOnly = p.filter(f => !existingIds.has(f.id));
                return [...offlineFiles, ...optimisticOnly];
            }));
        }, 150);
        return () => clearTimeout(timer);
    }, [plantTourId, isOfflineStarted]);

    // Auto-populate start form for cycle N when cycle N-1 becomes completed
    useEffect(() => {
        const initial = getBakingCoolingInitial();
        if (!initial?.productName && !initial?.lineNumber && !initial?.executiveName && !initial?.bakingTime) return;
        setFormDataByCycle(prev => prev.map((p, i) => {
            const cycleNum = i + 1;
            if (cycleNum === 1) {
                const isEmpty = !p.productName && !p.lineNumber && !p.executiveName && !p.bakingTime;
                if (!isEmpty) return p;
                return { productName: initial?.productName ?? '', lineNumber: initial?.lineNumber ?? '', executiveName: initial?.executiveName ?? '', bakingTime: initial?.bakingTime ?? '' };
            }
            if (!cycleCompleted[cycleNum - 2]) return p; // previous cycle not completed
            const isEmpty = !p.productName && !p.lineNumber && !p.executiveName && !p.bakingTime;
            if (!isEmpty) return p;
            return { productName: initial?.productName ?? '', lineNumber: initial?.lineNumber ?? '', executiveName: initial?.executiveName ?? '', bakingTime: initial?.bakingTime ?? '' };
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cycleCompleted]);

    return (
        <DashboardLayout>
            <div className="w-full">
                {/* Header Section */}
                <div className="w-full mb-6">
                    {/* Back Button */}
                    <div className="px-4 md:px-6 py-3">
                        <button
                            onClick={handleBack}
                            className="flex items-center text-gray-800 hover:text-gray-900 transition-colors"
                        >
                            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            <span className="font-medium">Back</span>
                        </button>
                    </div>

                    {/* Information Bar */}
                    <div className="bg-gray-50 px-4 md:px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            {/* Left Side - Title and Details */}
                            <div className="flex items-center gap-3">
                                <h1 className="text-xl font-bold text-gray-800">Baking & Cooling</h1>
                                {/* Shift Tag - Pill Shaped */}
                                <div className="flex items-center gap-1.5 bg-gray-200 border border-gray-300 rounded-full px-3 py-1">
                                    <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                                    <span className="text-sm text-gray-700">{selectedCycle || 'Shift 1'}</span>
                                </div>
                                {/* Date Separator and Date */}
                                <span className="text-gray-600">•</span>
                                <span className="text-sm text-gray-700">{formattedDate}</span>
                            </div>

                            {/* Right Side - Three Dots Menu */}
                            <button className="text-gray-600 hover:text-gray-800">
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content - Cycles (CodeVerification pattern: completed list + current cycle) */}
                <div className="w-full px-4 md:px-6 space-y-6">
                    {/* Completed Cycles */}
                    {Array.from({ length: MAX_CYCLES }, (_, i) => i + 1).filter((n) => cycleCompleted[idx(n)] || savedProducts[idx(n)].length > 0).map((n) => (
                        <div key={n} className="light-surface bg-white rounded-lg shadow-lg p-6">
                            {/* Cycle Header with Checkmark and Toggle */}
                            <div 
                                className="flex items-center justify-between mb-6 cursor-pointer"
                                onClick={() => toggleCompletedCycleExpansion(n)}
                            >
                                <div className="flex items-center gap-3">
                                    <h2 className="text-xl font-bold text-gray-800">Cycle {n}</h2>
                                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-600">({savedProducts[idx(n)].length} product(s))</span>
                                </div>
                                <svg 
                                    className={`w-5 h-5 text-gray-500 transition-transform ${expandedCompletedCycles.has(n) ? 'rotate-180' : ''}`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>

                            {/* Expanded Content - Full Product Details */}
                            {expandedCompletedCycles.has(n) && (
                                <>
                                    {savedProducts[idx(n)].length > 0 ? (
                                        savedProducts[idx(n)].map((productData, index) => (
                                            <div key={index} className="mb-6 border-b border-gray-200 pb-6 last:border-b-0">
                                                {/* Header Information - Light Blue Band */}
                                                <div className="bg-blue-100 rounded-lg p-4 mb-6">
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                        <div>
                                                            <div className="text-xs text-gray-600 mb-1">Product</div>
                                                            <div className="text-sm font-semibold text-gray-800">{productData.product || 'N/A'}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                                                            <div className="text-sm font-semibold text-gray-800">{productData.executiveName || 'N/A'}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-xs text-gray-600 mb-1">Line Number</div>
                                                            <div className="text-sm font-semibold text-gray-800">{productData.lineNumber || 'N/A'}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-xs text-gray-600 mb-1">Baking Time</div>
                                                            <div className="text-sm font-semibold text-gray-800">{productData.bakingTime || 'N/A'}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Summary Section */}
                                                <div className="mb-6">
                                                    <h3 className="text-sm font-semibold text-red-600 mb-4">Summary</h3>
                                                    <div className="space-y-3">
                                                        {/* Line Number */}
                                                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                            <span className="text-sm text-gray-700">Line Number</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.lineNumber || ''}</span>
                                                        </div>

                                                        {/* Baking Temp Zones */}
                                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Baking Temp Zone 1</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.bakingTempZone1 || ''}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Baking Temp Zone 2</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.bakingTempZone2 || ''}</span>
                                                            </div>
                                                        </div>
                                                        <div className="py-2 border-b border-gray-200">
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Baking Temp Zone 3</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.bakingTempZone3 || ''}</span>
                                                            </div>
                                                        </div>

                                                        {/* Product Temp after Baking */}
                                                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                            <span className="text-sm text-gray-700">Product Temp after Baking</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.productTempAfterBaking || ''}</span>
                                                        </div>

                                                        {/* Oven Times */}
                                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Oven Fire Start time</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.ovenFireStartTime || ''}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Oven Stop Time</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.ovenStopTime || ''}</span>
                                                            </div>
                                                        </div>

                                                        {/* Product Times */}
                                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Product In time</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.productInTime || ''}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Product out time</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.productOutTime || ''}</span>
                                                            </div>
                                                        </div>

                                                        {/* Cooling Time */}
                                                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                            <span className="text-sm text-gray-700">Cooling Time</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.coolingTime || ''}</span>
                                                        </div>

                                                        {/* Cooling Temp, Core Temp, Product color */}
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2 border-b border-gray-200">
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Cooling Temp</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.coolingTemp || ''}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Core Temp</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.coreTemp || ''}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-sm text-gray-700">Product color</span>
                                                                <span className="text-sm font-medium text-gray-900">{productData.productColor || ''}</span>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                                {/* Attachments for this product */}
                                                {(() => {
                                                    // Only show files that belong to this cycle (cycleNum from document library); same product name can exist in different cycles
                                                    const cycleNumForDisplay = n;
                                                    const cycleFiles = (uploadedFiles[idx(n)] || []).filter(
                                                        (f) => (Number(f.cycleNum) || 1) === cycleNumForDisplay
                                                    );
                                                    const productKey = (productData.product || '').trim().toLowerCase();

                                                    // Match files by product name (case-insensitive, trimmed)
                                                    let filesForProduct = cycleFiles.filter((f) => {
                                                        if (!productKey) return false; // No product name to match
                                                        const fileProductName = (f.productName || '').trim().toLowerCase();
                                                        return fileProductName === productKey && fileProductName !== '';
                                                    });

                                                    // If no files match by product name, check if there are unscoped files (files without product names)
                                                    // For completed cycles, show unscoped files distributed by product index
                                                    if (filesForProduct.length === 0 && cycleFiles.length > 0) {
                                                        const unscopedFiles = cycleFiles.filter((f) => !f.productName || (f.productName || '').trim() === '');
                                                        const totalProducts = savedProducts[idx(n)].length;
                                                        
                                                        if (unscopedFiles.length > 0 && totalProducts > 0 && typeof index === 'number') {
                                                            // Distribute unscoped files evenly across products
                                                            const filesPerProduct = Math.floor(unscopedFiles.length / totalProducts);
                                                            const remainder = unscopedFiles.length % totalProducts;
                                                            const startIndex = index * filesPerProduct + Math.min(index, remainder);
                                                            const endIndex = startIndex + filesPerProduct + (index < remainder ? 1 : 0);
                                                            
                                                            const sortedUnscoped = unscopedFiles.sort((a, b) => a.uploadTime - b.uploadTime);
                                                            filesForProduct = sortedUnscoped.slice(startIndex, endIndex);
                                                        }
                                                    }

                                                    return (
                                                        <div className="mb-6">
                                                            <h3 className="text-sm font-semibold text-gray-700 mb-4">Attachments</h3>
                                                            {filesForProduct.length > 0 ? (
                                                                <div className="space-y-3">
                                                                    {filesForProduct.map((file) => (
                                                                        <div key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200">
                                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                </svg>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="text-sm font-medium text-gray-900 truncate">{file.fileName}</div>
                                                                                    <div className="text-xs text-gray-500">
                                                                                        Modified: {new Date(file.uploadTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(file.uploadTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleViewFile(file);
                                                                                }}
                                                                                disabled={viewingFileLoading === file.id}
                                                                                className="text-xs text-blue-600 hover:underline px-3 py-1 disabled:opacity-50"
                                                                            >
                                                                                {viewingFileLoading === file.id ? 'Opening…' : 'View'}
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-sm text-gray-500">No attachments uploaded</p>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <p>No products saved for Cycle {n}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}

                    {/* Current Cycle - single card for 1, 2, ... n (CodeVerification style) */}
                    <div className="light-surface bg-white rounded-lg shadow-lg p-6">
                        {/* Current Cycle Title - Only show when not completed */}
                        {!cycleCompleted[idx(currentCycle)] && (
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Cycle {currentCycle}</h2>
                            {isSessionStarted[idx(currentCycle)] && (
                                <svg className="w-5 h-5 text-gray-500 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            )}
                        </div>
                        )}

                        {/* Start Session Form - Only when no products yet OR user clicked "Add New Product" (never show with green summary) */}
                        {!isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (savedProducts[idx(currentCycle)].length === 0 || showStartFormForNewProduct[idx(currentCycle)]) && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                    {/* Left Column */}
                                    <div className="space-y-6">
                                        {/* Product Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Product Name
                                            </label>
                                            <input
                                                type="text"
                                                name="productName"
                                                value={formDataByCycle[idx(currentCycle)].productName}
                                                onChange={(e) => handleInputChange(e, currentCycle)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter product name"
                                            />
                                        </div>

                                        {/* Line Number */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Line Number
                                            </label>
                                            <input
                                                type="text"
                                                name="lineNumber"
                                                value={formDataByCycle[idx(currentCycle)].lineNumber}
                                                onChange={(e) => handleInputChange(e, currentCycle)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter line number"
                                            />
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="space-y-6">
                                        {/* Executive Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Executive Name
                                            </label>
                                            <input
                                                type="text"
                                                name="executiveName"
                                                value={formDataByCycle[idx(currentCycle)].executiveName}
                                                onChange={(e) => handleInputChange(e, currentCycle)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter executive name"
                                            />
                                        </div>

                                        {/* Baking Time */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Baking Time
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="bakingTime"
                                                    value={formDataByCycle[idx(currentCycle)].bakingTime}
                                                    onChange={(e) => handleInputChange(e, currentCycle)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="--:--:--"
                                                    maxLength={8}
                                                    pattern="^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$"
                                                />
                                                {/* Clock Icon */}
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Start Session Button */}
                                <div className="flex justify-end">
                                    <button
                                        onClick={() => handleStartSession(currentCycle)}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Start Session
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Completed Cycle View - Show when cycle is completed */}
                        {cycleCompleted[idx(currentCycle)] && (
                            <>
                                {/* Cycle {currentCycle} Title with Checkmark and Toggle Arrow */}
                                <div 
                                    className="flex items-center justify-between mb-6 cursor-pointer"
                                    onClick={() => setCycleExpandedAt(currentCycle, !cycleExpanded[idx(currentCycle)])}
                                >
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-xl font-bold text-gray-800">Cycle {currentCycle}</h2>
                                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <svg 
                                        className={`w-5 h-5 text-gray-500 transition-transform ${cycleExpanded[idx(currentCycle)] ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>

                                {/* Multiple Products - Show all saved products when expanded */}
                                {cycleExpanded[idx(currentCycle)] && (
                                <>
                                {savedProducts[idx(currentCycle)].length > 0 ? (
                                    savedProducts[idx(currentCycle)].map((productData, index) => (
                                        <div key={index} className="mb-6 border-b border-gray-200 pb-6 last:border-b-0">
                                            {/* Header Information - Light Blue Band */}
                                            <div className="bg-blue-100 rounded-lg p-4 mb-6">
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div>
                                                        <div className="text-xs text-gray-600 mb-1">Product</div>
                                                        <div className="text-sm font-semibold text-gray-800">{productData.product || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                                                        <div className="text-sm font-semibold text-gray-800">{productData.executiveName || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-gray-600 mb-1">Line Number</div>
                                                        <div className="text-sm font-semibold text-gray-800">{productData.lineNumber || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-gray-600 mb-1">Baking Time</div>
                                                        <div className="text-sm font-semibold text-gray-800">{productData.bakingTime || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Summary Section */}
                                            <div className="mb-6">
                                                <h3 className="text-sm font-semibold text-red-600 mb-4">Summary</h3>
                                                <div className="space-y-3">
                                                    {/* Line Number */}
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                        <span className="text-sm text-gray-700">Line Number</span>
                                                        <span className="text-sm font-medium text-gray-900">{productData.lineNumber || ''}</span>
                                                    </div>

                                                    {/* Baking Temp Zones */}
                                                    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Baking Temp Zone 1</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.bakingTempZone1 || ''}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Baking Temp Zone 2</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.bakingTempZone2 || ''}</span>
                                                        </div>
                                                    </div>
                                                    <div className="py-2 border-b border-gray-200">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Baking Temp Zone 3</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.bakingTempZone3 || ''}</span>
                                                        </div>
                                                    </div>

                                                    {/* Product Temp after Baking */}
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                        <span className="text-sm text-gray-700">Product Temp after Baking</span>
                                                        <span className="text-sm font-medium text-gray-900">{productData.productTempAfterBaking || ''}</span>
                                                    </div>


                                                    {/* Oven Times */}
                                                    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Oven Fire Start time</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.ovenFireStartTime || ''}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Oven Stop Time</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.ovenStopTime || ''}</span>
                                                        </div>
                                                    </div>

                                                    {/* Product Times */}
                                                    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-200">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Product In time</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.productInTime || ''}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Product out time</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.productOutTime || ''}</span>
                                                        </div>
                                                    </div>

                                                    {/* Cooling Time */}
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                        <span className="text-sm text-gray-700">Cooling Time</span>
                                                        <span className="text-sm font-medium text-gray-900">{productData.coolingTime || ''}</span>
                                                    </div>

                                                    {/* Cooling Temp, Core Temp, Product color */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2 border-b border-gray-200">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Cooling Temp</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.coolingTemp || ''}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Core Temp</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.coreTemp || ''}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-700">Product color</span>
                                                            <span className="text-sm font-medium text-gray-900">{productData.productColor || ''}</span>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                            {/* Attachments for this product: only show files for this cycle (cycleNum from document library) */}
                                            {(() => {
                                                const cycleNumForDisplay = currentCycle;
                                                const cycleFiles = (uploadedFiles[idx(currentCycle)] || []).filter(
                                                    (f) => (Number(f.cycleNum) || 1) === cycleNumForDisplay
                                                );
                                                const productKey = (productData.product || '').trim().toLowerCase();

                                                // Match files by product name (case-insensitive, trimmed)
                                                let filesForProduct = cycleFiles.filter((f) => {
                                                    if (!productKey) return false; // No product name to match
                                                    const fileProductName = (f.productName || '').trim().toLowerCase();
                                                    return fileProductName === productKey && fileProductName !== '';
                                                });

                                                // If no files match by product name, check if there are unscoped files (files without product names)
                                                // For completed cycles, show unscoped files distributed by product index
                                                if (filesForProduct.length === 0 && cycleFiles.length > 0) {
                                                    const unscopedFiles = cycleFiles.filter((f) => !f.productName || (f.productName || '').trim() === '');
                                                    const totalProducts = savedProducts[idx(currentCycle)].length;
                                                    
                                                    if (unscopedFiles.length > 0 && totalProducts > 0 && typeof index === 'number') {
                                                        // Distribute unscoped files evenly across products
                                                        const filesPerProduct = Math.floor(unscopedFiles.length / totalProducts);
                                                        const remainder = unscopedFiles.length % totalProducts;
                                                        const startIndex = index * filesPerProduct + Math.min(index, remainder);
                                                        const endIndex = startIndex + filesPerProduct + (index < remainder ? 1 : 0);
                                                        
                                                        const sortedUnscoped = unscopedFiles.sort((a, b) => a.uploadTime - b.uploadTime);
                                                        filesForProduct = sortedUnscoped.slice(startIndex, endIndex);
                                                    }
                                                }

                                                return (
                                                    <div className="mb-6">
                                                        <h3 className="text-sm font-semibold text-gray-700 mb-4">Attachments</h3>
                                                        {filesForProduct.length > 0 ? (
                                                            <div className="space-y-3">
                                                                {filesForProduct.map((file) => (
                                                                    <div key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200">
                                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                            </svg>
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="text-sm font-medium text-gray-900 truncate">{file.fileName}</div>
                                                                                <div className="text-xs text-gray-500">
                                                                                    Modified: {new Date(file.uploadTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(file.uploadTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleViewFile(file);
                                                                            }}
                                                                            disabled={viewingFileLoading === file.id}
                                                                            className="text-xs text-blue-600 hover:underline px-3 py-1 disabled:opacity-50"
                                                                        >
                                                                            {viewingFileLoading === file.id ? 'Opening…' : 'View'}
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">No attachments uploaded</p>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No products saved for Cycle {currentCycle}</p>
                                    </div>
                                )}
                                </>
                                )}
                            </>
                        )}

                        {/* Summary Section - Hide when "Add New Product" clicked so start section can show */}
                        {savedProducts[idx(currentCycle)].length > 0 && !cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && !showStartFormForNewProduct[idx(currentCycle)] && (
                            <div className="mb-6">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        <span className="text-green-700 font-semibold">
                                            {savedProducts[idx(currentCycle)].length} product(s) saved in Cycle {currentCycle}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowStartFormForNewProductAt(currentCycle, true);
                                                setIsSessionStartedAt(1, false);
                                                const initial = getBakingCoolingInitial();
                                                setFormDataByCycleAt(currentCycle, {
                                                    productName: initial?.productName ?? '',
                                                    lineNumber: initial?.lineNumber ?? '',
                                                    executiveName: initial?.executiveName ?? '',
                                                    bakingTime: initial?.bakingTime ?? ''
                                                });
                                                setSaveFormDataByCycleAt(currentCycle, {
                                                    bakingTempZone1: '',
                                                    bakingTempZone2: '',
                                                    bakingTempZone3: '',
                                                    bakingTempZone4: '',
                                                    productTempAfterBaking: '',
                                                    ap: '',
                                                    std: '',
                                                    ad: '',
                                                    ovenFireStartTime: '',
                                                    ovenStopTime: '',
                                                    productInTime: '',
                                                    productOutTime: '',
                                                    coolingTime: '',
                                                    coolingTemp: '',
                                                    coreTemp: '',
                                                    productColor: ''
                                                });
                                                // Reset uploaded files for new product
                                                setUploadedFilesAt(currentCycle, () => [] as FileDisplayData[]);
                                                setSelectedFileAt(currentCycle, null);
                                            }}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                                        >
                                            Add New Product
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleCompleteCycle(currentCycle)}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                                        >
                                            Complete Cycle & Move to Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Save Checklist Form - Show when session started and not completed */}
                        {isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (
                            <>
                                {/* Header Information - Light Blue Band */}
                                <div className="bg-blue-100 rounded-lg p-4 mb-6">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <div className="text-xs text-gray-600 mb-1">Product Name</div>
                                            <div className="text-sm font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].productName || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                                            <div className="text-sm font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].executiveName || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-600 mb-1">Line Number</div>
                                            <div className="text-sm font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].lineNumber || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-600 mb-1">Baking Time</div>
                                            <div className="text-sm font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].bakingTime || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Baking Temperature Data */}
                                <div className="mb-6">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Baking Temperature Data</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {[
                                            { name: 'bakingTempZone1', label: 'Baking Temp Zone 1' },
                                            { name: 'bakingTempZone2', label: 'Baking Temp Zone 2' },
                                            { name: 'bakingTempZone3', label: 'Baking Temp Zone 3' },
                                            { name: 'productTempAfterBaking', label: 'Product Temp after Baking' }
                                        ].map((field) => (
                                            <div key={field.name} className="min-w-0">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
                                                <input
                                                    type="text"
                                                    name={field.name}
                                                    value={saveFormDataByCycle[idx(currentCycle)][field.name as keyof typeof emptySaveForm]}
                                                    onChange={(e) => handleInputChange(e, currentCycle, true)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Enter value"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Oven Timing Data */}
                                <div className="mb-6">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Oven Timing Data</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {[
                                            { name: 'ovenFireStartTime', label: 'Oven Fire Start time' },
                                            { name: 'ovenStopTime', label: 'Oven Stop Time' },
                                            { name: 'productInTime', label: 'Product In time' },
                                            { name: 'productOutTime', label: 'Product out time' }
                                        ].map((field) => (
                                            <div key={field.name}>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        name={field.name}
                                                        value={saveFormDataByCycle[idx(currentCycle)][field.name as keyof typeof emptySaveForm]}
                                                        onChange={(e) => handleInputChange(e, currentCycle, true)}
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="--:--:--"
                                                        maxLength={8}
                                                        pattern="^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$"
                                                    />
                                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Cooling Data */}
                                <div className="mb-6">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Cooling Data</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Cooling time</label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="coolingTime"
                                                    value={saveFormDataByCycle[idx(currentCycle)].coolingTime}
                                                    onChange={(e) => handleInputChange(e, currentCycle, true)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="--:--:--"
                                                    maxLength={8}
                                                    pattern="^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$"
                                                />
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                        {[
                                            { name: 'coolingTemp', label: 'Cooling temp' },
                                            { name: 'coreTemp', label: 'Core temp' },
                                            { name: 'productColor', label: 'Product color' }
                                        ].map((field) => (
                                            <div key={field.name}>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">{field.label}</label>
                                                <input
                                                    type="text"
                                                    name={field.name}
                                                    value={saveFormDataByCycle[idx(currentCycle)][field.name as keyof typeof emptySaveForm]}
                                                    onChange={(e) => handleInputChange(e, currentCycle, true)}
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Enter value"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Upload Attachment */}
                                <div className="mb-6">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Upload Attachment</h3>
                                    <div className="border border-gray-300 rounded-md p-4">
                                        <div className="flex items-center gap-4 mb-2">
                                            <label className="cursor-pointer">
                                                <span className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-200">
                                                    Choose File
                                                </span>
                                                <input 
                                                    type="file" 
                                                    id={`attachment-${currentCycle}`}
                                                    className="hidden" 
                                                    onChange={(e) => handleFileSelect(e, currentCycle)}
                                                />
                                            </label>
                                            <span className="text-sm text-gray-500">
                                                {selectedFile[idx(currentCycle)]?.name ?? 'No file chosen'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 mb-3">You can upload supporting documents or images here.</p>
                                        <button 
                                            onClick={() => handleFileUpload(currentCycle)}
                                            disabled={!selectedFile[idx(currentCycle)] || isUploading[idx(currentCycle)]}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                        >
                                            {isUploading[idx(currentCycle)] ? 'Uploading...' : 'Upload'}
                                        </button>

                                        {/* Display uploaded files: only files for this cycle (cycleNum from document library), then by product */}
                                        {(() => {
                                            const cycleNumForDisplay = currentCycle;
                                            const cycleFiles = (uploadedFiles[idx(currentCycle)] || []).filter(
                                                (f) => (Number(f.cycleNum) || 1) === cycleNumForDisplay
                                            );
                                            const savedProductsCount = savedProducts[idx(currentCycle)].length;
                                            const currentProduct = (formDataByCycle[idx(currentCycle)].productName || '').trim().toLowerCase();
                                            
                                            let filesForProduct: FileDisplayData[] = [];
                                            
                                            // If it's the first product in cycle, show all files
                                            if (savedProductsCount === 0) {
                                                filesForProduct = cycleFiles;
                                            } else {
                                                // For subsequent products, match by product name (case-insensitive)
                                                const withProductName = cycleFiles.filter((f) => {
                                                    const fileProductName = (f.productName || '').trim().toLowerCase();
                                                    return fileProductName === currentProduct && fileProductName !== '';
                                                });
                                                
                                                if (withProductName.length > 0) {
                                                    // Found files with matching product name
                                                    filesForProduct = withProductName;
                                                } else {
                                                    // No files with matching product name
                                                    // Show unscoped files that belong to this product based on upload order
                                                    const unscopedSorted = cycleFiles
                                                        .filter((f) => (f.productName || '').trim() === '')
                                                        .slice()
                                                        .sort((a, b) => a.uploadTime - b.uploadTime);
                                                    
                                                    // Distribute unscoped files: product index = savedProductsCount (the product currently being worked on)
                                                    const totalUnscoped = unscopedSorted.length;
                                                    const totalProductsIncludingCurrent = savedProductsCount + 1; // Including the current product being worked on
                                                    
                                                    if (totalUnscoped > 0) {
                                                        const filesPerProduct = Math.floor(totalUnscoped / totalProductsIncludingCurrent);
                                                        const remainder = totalUnscoped % totalProductsIncludingCurrent;
                                                        const startIndex = savedProductsCount * filesPerProduct + Math.min(savedProductsCount, remainder);
                                                        const endIndex = startIndex + filesPerProduct + (savedProductsCount < remainder ? 1 : 0);
                                                        
                                                        filesForProduct = unscopedSorted.slice(startIndex, endIndex);
                                                    }
                                                }
                                            }
                                            return (
                                            <div className="mt-4">
                                                <h4 className="text-xs font-medium text-gray-700 mb-2">Uploaded Files:</h4>
                                                {filesForProduct.length > 0 ? (
                                                <div className="space-y-2">
                                                    {filesForProduct.map((file) => (
                                                        <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                <span className="text-xs text-gray-600 truncate">{file.fileName}</span>
                                                                <span className="text-xs text-gray-400">
                                                                    ({(file.fileSize / 1024).toFixed(2)} KB)
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                {file.fileUrl && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleViewFile(file)}
                                                                        disabled={viewingFileLoading === file.id}
                                                                        className="text-xs text-blue-600 hover:underline px-2 py-1 disabled:opacity-50"
                                                                    >
                                                                        {viewingFileLoading === file.id ? 'Opening…' : 'View'}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteFile(file)}
                                                                    disabled={deletingFileId === file.id}
                                                                    className="text-xs text-red-600 hover:underline px-2 py-1 disabled:opacity-50"
                                                                >
                                                                    {deletingFileId === file.id ? 'Deleting…' : 'Delete'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                ) : (
                                                    <p className="text-xs text-gray-500">No files uploaded yet. Choose a file above and click Upload.</p>
                                                )}
                                            </div>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-4">
                                    <button
                                        onClick={() => handleCancelSession(currentCycle)}
                                        className="px-6 py-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 transition-colors font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleSaveSession(currentCycle)}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Save Checklist
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                </div>
            </div>

            {/* File Viewer Modal */}
            {viewingFile && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" 
                    onClick={() => {
                        if (viewingFile.blobUrl) {
                            URL.revokeObjectURL(viewingFile.blobUrl);
                        }
                        setViewingFile(null);
                    }}
                >
                    <div 
                        className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-auto" 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800 truncate flex-1 mr-4">
                                {viewingFile.file.fileName}
                            </h3>
                            <button
                                onClick={() => {
                                    if (viewingFile.blobUrl) {
                                        URL.revokeObjectURL(viewingFile.blobUrl);
                                    }
                                    setViewingFile(null);
                                }}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <div className="p-4">
                            {isImageFile(viewingFile.file.fileName) ? (
                                <img
                                    src={viewingFile.blobUrl}
                                    alt={viewingFile.file.fileName}
                                    className="max-w-full h-auto mx-auto rounded-lg"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not available%3C/text%3E%3C/svg%3E';
                                    }}
                                />
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600 mb-4">Preview not available for this file type.</p>
                                    <a
                                        href={viewingFile.blobUrl}
                                        download={viewingFile.file.fileName}
                                        className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Download File
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default BakingAndCooling;
